import { SortMeta } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { HisSearchResultRow } from '../model/his-search-result';

/**
 * This class provides common utility methods for the generic search.
 */
export class GenericSearchUtil {

    /**
     * Checks if the given path is an LID path.
     * 
     * A path is considered an LID path if it ends with '.lid' or 'Obj.id'.
     * 
     * @param path the path to check.
     * @returns true if the path is a LID path, false otherwise.
     */
    public static isLidPath( path: string ) {
        return path.endsWith( '.lid' ) || path.endsWith( 'Obj.id' );
    }

    /**
     * Converts a selection value from a tree that might be a selection including subnodes to a clean ID or LID which is a number.
     * 
     * A selection value that includes subnodes has an 'x' at the end. This will be removed
     * and the value will be converted to a number.
     * 
     * @param value the value to convert.
     * @returns the number which might be a LID or ID.
     */
    public static idOrLidTreeSelectionToCleanIdOrLid( value: string ): number | null {
        if ( !value ) {
            return null;
        }

        return +value.replace( 'x', '' );
    }

    /**
     * Checks if the given value is a tree selection that includes subnodes.
     * 
     * A selection value that includes subnodes has an 'x' at the end.
     * 
     * @param value the value to check.
     * @returns true if the value is a tree selection including subnodes, false otherwise.
     */
    public static isIdOrLidTreeSelectionIncludingSubnodes( value: string ): boolean {
        if ( !value ) {
            return false;
        }

        return value.endsWith( 'x' );
    }

    /**
     * Checks whether a his-search-autocomplete should be initialized lazy.
     * 
     * For this, we check if selectFrom contains '.', which indicates a service method call.
     * Also, for a lazy initialization, showDropdownIcon must be false.
     * 
     * @param selectFrom the selectFrom expression
     * @param showDropdownIcon whether a dropdown icon is shown
     * @returns true if the parameters indicate lazy initialization, false otherwise.
     */
    public static isLazySelectFrom( selectFrom: string, showDropdownIcon: boolean ): boolean {
        return selectFrom && selectFrom.includes( '.' ) && showDropdownIcon === false;
    }

    /**
     * Sorts data using specified criteria
     * @param data The data to sort
     * @param sortMeta The sorting metadata from PrimeNG
     * @returns Sorted copy of the data
     */
    public static sortData(data: HisSearchResultRow[], sortMeta: SortMeta[]): HisSearchResultRow[] {
        return this.sortDataInPlace( [...data], sortMeta );
    }

    /**
     * Sorts data in place using specified criteria.\
     * Can be used as a custom sort function for PrimeNG tables to avoid unnecessary copying of data on each sort action.
     * @param data The data to sort
     * @param sortMeta The sorting metadata from PrimeNG
     * @returns The sorted data
     */
    public static sortDataInPlace( data: HisSearchResultRow[], sortMeta: SortMeta[] ): HisSearchResultRow[] {
        if ( !sortMeta || sortMeta.length === 0 ) {
            return data;
        }

        return data.sort( ( a, b ) => {
            for ( const meta of sortMeta ) {
                const valueA = a.get( meta.field );
                const valueB = b.get( meta.field );
                const comparator = meta.order === 1 ? 1 : -1;

                // Handle null values
                if ( valueA == null && valueB == null ) continue;
                if ( valueA == null ) return comparator;
                if ( valueB == null ) return -comparator;

                // Handle specific types
                const result = this.compareValues( valueA, valueB );
                if ( result !== 0 ) {
                    return result * comparator;
                }
            }
            return 0;
        } );
    }
    
    /**
     * Compares two values of potentially different types
     * @param valueA First value to compare
     * @param valueB Second value to compare
     * @returns Comparison result (-1, 0, 1)
     */
    private static compareValues(valueA: any, valueB: any): number {
        // Handle booleans
        if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
            return valueA === valueB ? 0 : (valueA ? 1 : -1);
        }
    
        // Handle dates
        const dateA = this.toDate(valueA);
        const dateB = this.toDate(valueB);
        if (dateA && dateB) {
            return dateA.getTime() - dateB.getTime();
        }
    
        // Handle numbers
        const numA = !isNaN(Number(valueA)) ? Number(valueA) : null;
        const numB = !isNaN(Number(valueB)) ? Number(valueB) : null;
        if (numA !== null && numB !== null) {
            return numA - numB;
        }
    
        // Handle strings
        if (typeof valueA === 'string' && typeof valueB === 'string') {
            return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
        }
    
        // Default comparison
        return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
    }
    
    /**
     * Converts a value to a Date object if possible
     * @param value The value to convert
     * @returns Date object or null if conversion fails
     */
    private static toDate(value: any): Date | null {
        if (value instanceof Date) {
            return value;
        }
    
        if (typeof value === 'string') {
            // Try to parse the date assuming the format dd.MM.yyyy
            const parts = value.split('.');
            if (parts.length === 3) {
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1; // Month is 0-based
                const year = parseInt(parts[2], 10);
                const date = new Date(year, month, day);
    
                // Check if the date is valid
                return isNaN(date.getTime()) ? null : date;
            }
    
            // Try standard date parsing
            const date = new Date(value);
            return isNaN(date.getTime()) ? null : date;
        }
    
        return null;
    }
    
    /**
     * Helper method to extract sort metadata from PrimeNG {@link TableLazyLoadEvent}
     * @param event The lazy load event from PrimeNG table
     * @returns Array of SortMeta objects
     */
    public static extractSortMeta( event: TableLazyLoadEvent ): SortMeta[] {
        if ( !event.sortField ) {
            return event.multiSortMeta || [];
        }

        const sortOrder = event.sortOrder || 1;

        if ( Array.isArray( event.sortField ) ) {
            return [{ field: event.sortField.join( ',' ), order: sortOrder }];
        }

        return [{ field: event.sortField, order: sortOrder }];
    }

}
